// The studio presenters: professional flat-illustration people drawn with the canvas, animated from the mouth / blink / head state of lipsync.js.
// Everything is drawn in "face units": 1 unit = half the width of the face, (0, 0) = the middle of the face, y grows downwards.
//   forehead top -1.2   eyes -0.12   brows -0.38   nose tip 0.34   mouth 0.68   chin 1.25   shoulders from 1.75

export function shade(hex, amount) {
  const n = parseInt(String(hex).replace("#", ""), 16);
  const channel = (shift) => {
    const v = (n >> shift) & 255;
    return Math.round(amount >= 0 ? v + (255 - v) * amount : v * (1 + amount));
  };
  return `rgb(${channel(16)},${channel(8)},${channel(0)})`;
}

export const PRESENTERS = {
  ayaan: { name: "Ayaan", skin: "#dba67b", hair: "#1c1612", hairStyle: "slick", brows: 1.1, eye: "#4b3324", beard: "stubble", outfit: { type: "suit", color: "#1f3052", shirt: "#f5f7fb", tie: "#b52a30" } },
  sara: { name: "Sara", skin: "#f1c6a2", hair: "#2a1913", hairStyle: "long", brows: 0.8, eye: "#5a3a26", lashes: true, lips: "#c1566a", outfit: { type: "blazer", color: "#2b5b91", inner: "#f8e9e2" } },
  zainab: { name: "Zainab", skin: "#e2b28c", hair: "#222222", hairStyle: "hijab", scarf: "#2d807a", brows: 0.85, eye: "#3f2a1d", lashes: true, lips: "#b9505f", outfit: { type: "blazer", color: "#23495a", inner: "#ffffff" } },
  hamza: { name: "Hamza", skin: "#c99670", hair: "#15110e", hairStyle: "short", brows: 1.25, eye: "#2f2016", beard: "full", outfit: { type: "kurta", color: "#f2eee5", collar: "#d9d2c0" } },
  emma: { name: "Emma", skin: "#f6d6bf", hair: "#d7b068", hairStyle: "bob", brows: 0.75, eye: "#4f7fa6", lashes: true, lips: "#cc6f78", glasses: "round", outfit: { type: "sweater", color: "#8a5c84" } },
  daniel: { name: "Daniel", skin: "#f2cba7", hair: "#5b3a22", hairStyle: "side", brows: 1.0, eye: "#556b4a", glasses: "rect", outfit: { type: "shirt", color: "#5a8db8", collar: "#e3ecf4" } },
  noor: { name: "Noor", skin: "#c68d64", hair: "#100d0c", hairStyle: "ponytail", brows: 0.9, eye: "#2d1c12", lashes: true, lips: "#a8404f", outfit: { type: "blazer", color: "#8d3a58", inner: "#ffffff" } },
  omar: { name: "Omar", skin: "#d0a077", hair: "#c3c7cc", hairStyle: "short", hairline: 0.16, brows: 1.15, eye: "#3d2a1c", mustache: true, glasses: "rect", outfit: { type: "suit", color: "#4b5059", shirt: "#ffffff", tie: "#2c5083" } },
};
export const PRESENTER_IDS = Object.keys(PRESENTERS);

const ell = (ctx, x, y, rx, ry) => { ctx.beginPath(); ctx.ellipse(x, y, Math.max(0.0001, rx), Math.max(0.0001, ry), 0, 0, Math.PI * 2); };

// ------------------------------------------------------------------------------------------ the body
function drawTorso(ctx, spec, breathe) {
  const o = spec.outfit;
  ctx.save();
  ctx.scale(1, breathe);
  const body = () => {
    ctx.beginPath();
    ctx.moveTo(-0.5, 1.7);
    ctx.bezierCurveTo(-1.2, 1.85, -2.1, 2.05, -2.45, 2.7);
    ctx.bezierCurveTo(-2.75, 3.3, -2.8, 4.2, -2.85, 5.4);
    ctx.lineTo(2.85, 5.4);
    ctx.bezierCurveTo(2.8, 4.2, 2.75, 3.3, 2.45, 2.7);
    ctx.bezierCurveTo(2.1, 2.05, 1.2, 1.85, 0.5, 1.7);
    ctx.closePath();
  };
  // the neck (under the head; a shadow under the chin comes from the head's own drawing)
  ctx.fillStyle = shade(spec.skin, -0.1);
  ctx.beginPath();
  ctx.moveTo(-0.44, 0.95);
  ctx.lineTo(-0.46, 1.95);
  ctx.quadraticCurveTo(0, 2.2, 0.46, 1.95);
  ctx.lineTo(0.44, 0.95);
  ctx.closePath();
  ctx.fill();

  const color = spec.hairStyle === "hijab" ? o.color : o.color;
  const g = ctx.createLinearGradient(0, 1.7, 0, 4.6);
  g.addColorStop(0, shade(color, 0.16));
  g.addColorStop(0.5, color);
  g.addColorStop(1, shade(color, -0.22));
  ctx.fillStyle = g;
  body();
  ctx.fill();

  if (o.type === "suit" || o.type === "blazer") {
    // the shirt or top showing in the opening of the jacket
    ctx.fillStyle = o.type === "suit" ? o.shirt : o.inner;
    ctx.beginPath();
    if (o.type === "suit") { ctx.moveTo(-0.55, 1.72); ctx.lineTo(0, 3.5); ctx.lineTo(0.55, 1.72); ctx.quadraticCurveTo(0, 1.95, -0.55, 1.72); }
    else { ctx.moveTo(-0.62, 1.74); ctx.quadraticCurveTo(0, 3.15, 0.62, 1.74); ctx.quadraticCurveTo(0, 1.98, -0.62, 1.74); }
    ctx.closePath();
    ctx.fill();
    // lapels
    ctx.fillStyle = shade(color, -0.12);
    ctx.beginPath();
    ctx.moveTo(-0.55, 1.72); ctx.lineTo(-1.12, 2.55); ctx.lineTo(-0.5, 3.85); ctx.lineTo(0, 3.5); ctx.closePath();
    ctx.fill();
    ctx.beginPath();
    ctx.moveTo(0.55, 1.72); ctx.lineTo(1.12, 2.55); ctx.lineTo(0.5, 3.85); ctx.lineTo(0, 3.5); ctx.closePath();
    ctx.fill();
    ctx.strokeStyle = shade(color, 0.22);
    ctx.lineWidth = 0.025;
    ctx.beginPath(); ctx.moveTo(-0.55, 1.72); ctx.lineTo(-1.12, 2.55); ctx.lineTo(-0.5, 3.85); ctx.moveTo(0.55, 1.72); ctx.lineTo(1.12, 2.55); ctx.lineTo(0.5, 3.85); ctx.stroke();
    if (o.type === "suit") {
      // collar and tie
      ctx.fillStyle = shade(o.shirt, -0.04);
      ctx.beginPath(); ctx.moveTo(-0.5, 1.72); ctx.lineTo(-0.1, 2.45); ctx.lineTo(-0.62, 2.35); ctx.closePath(); ctx.fill();
      ctx.beginPath(); ctx.moveTo(0.5, 1.72); ctx.lineTo(0.1, 2.45); ctx.lineTo(0.62, 2.35); ctx.closePath(); ctx.fill();
      ctx.fillStyle = o.tie;
      ctx.beginPath(); ctx.moveTo(-0.13, 2.38); ctx.lineTo(0.13, 2.38); ctx.lineTo(0.17, 2.62); ctx.lineTo(-0.17, 2.62); ctx.closePath(); ctx.fill();
      ctx.fillStyle = shade(o.tie, -0.18);
      ctx.beginPath(); ctx.moveTo(-0.17, 2.62); ctx.lineTo(0.17, 2.62); ctx.lineTo(0.28, 4.3); ctx.lineTo(0, 4.55); ctx.lineTo(-0.28, 4.3); ctx.closePath(); ctx.fill();
      ctx.fillStyle = "rgba(255,255,255,0.14)";
      ctx.beginPath(); ctx.moveTo(-0.05, 2.7); ctx.lineTo(0.03, 2.7); ctx.lineTo(0.08, 4.2); ctx.lineTo(-0.1, 4.2); ctx.closePath(); ctx.fill();
    }
  } else if (o.type === "kurta") {
    ctx.fillStyle = o.collar;
    ctx.beginPath(); ctx.moveTo(-0.55, 1.72); ctx.quadraticCurveTo(0, 2.28, 0.55, 1.72); ctx.lineTo(0.5, 2.0); ctx.quadraticCurveTo(0, 2.55, -0.5, 2.0); ctx.closePath(); ctx.fill();
    ctx.strokeStyle = shade(color, -0.22); ctx.lineWidth = 0.03;
    ctx.beginPath(); ctx.moveTo(0, 2.3); ctx.lineTo(0, 4.4); ctx.stroke();
    ctx.fillStyle = shade(color, -0.3);
    [2.75, 3.25, 3.75].forEach((y) => { ell(ctx, 0.0, y, 0.035, 0.035); ctx.fill(); });
  } else if (o.type === "shirt") {
    ctx.fillStyle = shade(color, -0.35);
    ctx.beginPath(); ctx.moveTo(-0.52, 1.72); ctx.quadraticCurveTo(0, 2.45, 0.52, 1.72); ctx.quadraticCurveTo(0, 1.96, -0.52, 1.72); ctx.fill();
    ctx.fillStyle = o.collar;
    ctx.beginPath(); ctx.moveTo(-0.55, 1.7); ctx.lineTo(-0.2, 2.5); ctx.lineTo(-0.78, 2.28); ctx.closePath(); ctx.fill();
    ctx.beginPath(); ctx.moveTo(0.55, 1.7); ctx.lineTo(0.2, 2.5); ctx.lineTo(0.78, 2.28); ctx.closePath(); ctx.fill();
    ctx.strokeStyle = shade(color, -0.2); ctx.lineWidth = 0.025;
    ctx.beginPath(); ctx.moveTo(0, 2.5); ctx.lineTo(0, 4.4); ctx.stroke();
  } else if (o.type === "sweater") {
    ctx.strokeStyle = shade(color, 0.2); ctx.lineWidth = 0.17; ctx.lineCap = "round";
    ctx.beginPath(); ctx.moveTo(-0.55, 1.78); ctx.quadraticCurveTo(0, 2.38, 0.55, 1.78); ctx.stroke();
    ctx.strokeStyle = shade(color, -0.1); ctx.lineWidth = 0.02;
    for (let x = -2; x <= 2; x += 0.5) { ctx.beginPath(); ctx.moveTo(x, 2.8); ctx.lineTo(x * 1.04, 5.2); ctx.stroke(); }
  }
  if (spec.hairStyle === "hijab") {
    // the scarf wraps the neck and falls over the shoulders
    const s = spec.scarf;
    const sg = ctx.createLinearGradient(0, 1, 0, 3.8);
    sg.addColorStop(0, shade(s, -0.12));
    sg.addColorStop(1, shade(s, -0.28));
    ctx.fillStyle = sg;
    ctx.beginPath();
    ctx.moveTo(-1.15, 0.9); ctx.bezierCurveTo(-1.25, 1.9, -1.9, 2.5, -2.05, 3.5);
    ctx.quadraticCurveTo(0, 4.5, 2.05, 3.5);
    ctx.bezierCurveTo(1.9, 2.5, 1.25, 1.9, 1.15, 0.9);
    ctx.quadraticCurveTo(0, 2.3, -1.15, 0.9);
    ctx.closePath();
    ctx.fill();
    ctx.strokeStyle = "rgba(255,255,255,0.12)"; ctx.lineWidth = 0.03;
    ctx.beginPath(); ctx.moveTo(-0.6, 1.8); ctx.quadraticCurveTo(-0.7, 3, -1.1, 3.9); ctx.moveTo(0.5, 1.9); ctx.quadraticCurveTo(0.6, 3.1, 1.0, 4.0); ctx.stroke();
  }
  ctx.restore();
}

// ------------------------------------------------------------------------------------------ the head
const headPath = (ctx, grow = 0) => {
  ctx.beginPath();
  ctx.moveTo(0, 1.25 + grow);
  ctx.bezierCurveTo(0.47 + grow, 1.25 + grow, 0.97 + grow, 0.86, 1.0 + grow, 0.1);
  ctx.bezierCurveTo(1.04 + grow, -0.56, 0.78 + grow, -1.2 - grow, 0, -1.22 - grow);
  ctx.bezierCurveTo(-0.78 - grow, -1.2 - grow, -1.04 - grow, -0.56, -1.0 - grow, 0.1);
  ctx.bezierCurveTo(-0.97 - grow, 0.86, -0.47 - grow, 1.25 + grow, 0, 1.25 + grow);
  ctx.closePath();
};

function drawHairBack(ctx, spec) {
  const c = spec.hair;
  ctx.fillStyle = c;
  if (spec.hairStyle === "long") {
    ctx.beginPath();
    ctx.moveTo(-1.1, -0.2);
    ctx.bezierCurveTo(-1.5, 0.6, -1.5, 1.8, -1.6, 3.0);
    ctx.quadraticCurveTo(0, 3.6, 1.6, 3.0);
    ctx.bezierCurveTo(1.5, 1.8, 1.5, 0.6, 1.1, -0.2);
    ctx.closePath();
    ctx.fill();
  } else if (spec.hairStyle === "bob") {
    ctx.beginPath();
    ctx.moveTo(-1.08, -0.4);
    ctx.bezierCurveTo(-1.55, 0.3, -1.42, 1.0, -1.28, 1.35);
    ctx.quadraticCurveTo(0, 1.9, 1.28, 1.35);
    ctx.bezierCurveTo(1.42, 1.0, 1.55, 0.3, 1.08, -0.4);
    ctx.closePath();
    ctx.fill();
  } else if (spec.hairStyle === "ponytail") {
    ctx.beginPath();
    ctx.moveTo(0.9, -0.7);
    ctx.bezierCurveTo(1.6, -0.6, 1.9, 0.4, 1.7, 1.9);
    ctx.bezierCurveTo(1.55, 2.4, 1.25, 2.3, 1.2, 1.9);
    ctx.bezierCurveTo(1.25, 0.9, 1.1, 0.2, 0.8, 0.0);
    ctx.closePath();
    ctx.fill();
  } else if (spec.hairStyle === "hijab") {
    ctx.fillStyle = shade(spec.scarf, -0.18);
    ctx.beginPath();
    ctx.moveTo(-1.35, 0.4);
    ctx.bezierCurveTo(-1.65, -1.4, -0.9, -1.85, 0, -1.85);
    ctx.bezierCurveTo(0.9, -1.85, 1.65, -1.4, 1.35, 0.4);
    ctx.bezierCurveTo(1.35, 1.2, 1.2, 1.5, 1.15, 1.55);
    ctx.lineTo(-1.15, 1.55);
    ctx.bezierCurveTo(-1.2, 1.5, -1.35, 1.2, -1.35, 0.4);
    ctx.closePath();
    ctx.fill();
  }
}

function drawHairFront(ctx, spec) {
  const c = spec.hair;
  const sheen = shade(c, 0.28);
  const top = (lift = 0, hairline = 0) => {
    const h = hairline;
    ctx.beginPath();
    ctx.moveTo(-1.06, -0.18);
    ctx.bezierCurveTo(-1.18, -0.95 - lift, -0.72, -1.45 - lift, 0, -1.46 - lift);
    ctx.bezierCurveTo(0.72, -1.45 - lift, 1.18, -0.95 - lift, 1.06, -0.18);
    ctx.bezierCurveTo(1.02, -0.58 - h, 0.78, -0.86 - h, 0.4, -0.9 - h);
    ctx.quadraticCurveTo(0, -0.74 - h, -0.4, -0.92 - h);
    ctx.bezierCurveTo(-0.8, -0.88 - h, -1.0, -0.6 - h, -1.06, -0.18);
    ctx.closePath();
  };
  const style = spec.hairStyle;
  ctx.fillStyle = c;
  if (style === "short") {
    top(0, spec.hairline || 0);
    ctx.fill();
    ctx.strokeStyle = sheen; ctx.globalAlpha = 0.35; ctx.lineWidth = 0.05; ctx.lineCap = "round";
    ctx.beginPath(); ctx.moveTo(-0.55, -1.22); ctx.quadraticCurveTo(-0.1, -1.4, 0.45, -1.28); ctx.stroke(); ctx.globalAlpha = 1;
  } else if (style === "slick") {
    top(0.1, 0);
    ctx.fill();
    ctx.strokeStyle = shade(c, -0.35); ctx.lineWidth = 0.03;
    ctx.beginPath(); ctx.moveTo(-0.35, -1.5); ctx.quadraticCurveTo(-0.28, -1.15, -0.5, -0.88); ctx.stroke();
    ctx.strokeStyle = sheen; ctx.globalAlpha = 0.4; ctx.lineWidth = 0.06; ctx.lineCap = "round";
    ctx.beginPath(); ctx.moveTo(0.1, -1.38); ctx.quadraticCurveTo(0.55, -1.3, 0.85, -1.0); ctx.stroke(); ctx.globalAlpha = 1;
  } else if (style === "side") {
    ctx.beginPath();
    ctx.moveTo(-1.06, -0.1);
    ctx.bezierCurveTo(-1.2, -1.0, -0.7, -1.5, 0.05, -1.5);
    ctx.bezierCurveTo(0.8, -1.5, 1.2, -1.0, 1.06, -0.1);
    ctx.bezierCurveTo(1.0, -0.5, 0.85, -0.78, 0.55, -0.9);
    ctx.bezierCurveTo(0.2, -0.55, -0.35, -0.62, -0.78, -0.82);
    ctx.bezierCurveTo(-0.98, -0.6, -1.04, -0.35, -1.06, -0.1);
    ctx.closePath();
    ctx.fill();
    ctx.strokeStyle = sheen; ctx.globalAlpha = 0.35; ctx.lineWidth = 0.05; ctx.lineCap = "round";
    ctx.beginPath(); ctx.moveTo(-0.6, -1.3); ctx.quadraticCurveTo(0, -1.45, 0.6, -1.25); ctx.stroke(); ctx.globalAlpha = 1;
  } else if (style === "bob") {
    ctx.beginPath();
    ctx.moveTo(-1.1, 0.95);
    ctx.bezierCurveTo(-1.3, 0.2, -1.2, -1.0, -0.6, -1.4);
    ctx.bezierCurveTo(0.2, -1.65, 1.2, -1.2, 1.12, 0.0);
    ctx.bezierCurveTo(1.1, 0.4, 1.12, 0.7, 1.1, 0.95);
    ctx.bezierCurveTo(1.0, 0.7, 0.98, 0.3, 0.98, -0.1);
    ctx.bezierCurveTo(0.9, -0.6, 0.55, -0.88, 0.1, -0.9);
    ctx.bezierCurveTo(-0.4, -0.92, -0.78, -0.62, -0.98, -0.1);
    ctx.bezierCurveTo(-0.98, 0.3, -1.0, 0.7, -1.1, 0.95);
    ctx.closePath();
    ctx.fill();
    ctx.strokeStyle = sheen; ctx.globalAlpha = 0.4; ctx.lineWidth = 0.06; ctx.lineCap = "round";
    ctx.beginPath(); ctx.moveTo(-0.7, -1.28); ctx.quadraticCurveTo(0, -1.52, 0.7, -1.28); ctx.stroke(); ctx.globalAlpha = 1;
  } else if (style === "long") {
    ctx.beginPath();
    ctx.moveTo(-1.12, 1.9);
    ctx.bezierCurveTo(-1.45, 0.8, -1.3, -0.9, -0.55, -1.4);
    ctx.bezierCurveTo(0.3, -1.7, 1.25, -1.2, 1.12, 0.1);
    ctx.bezierCurveTo(1.12, 0.9, 1.25, 1.4, 1.2, 2.1);
    ctx.bezierCurveTo(1.08, 1.4, 1.0, 0.5, 0.97, -0.1);
    ctx.bezierCurveTo(0.85, -0.55, 0.45, -0.92, -0.15, -0.95);
    ctx.bezierCurveTo(-0.6, -0.9, -0.9, -0.55, -0.97, -0.05);
    ctx.bezierCurveTo(-1.0, 0.6, -1.05, 1.3, -1.12, 1.9);
    ctx.closePath();
    ctx.fill();
    ctx.strokeStyle = sheen; ctx.globalAlpha = 0.3; ctx.lineWidth = 0.07; ctx.lineCap = "round";
    ctx.beginPath(); ctx.moveTo(-0.7, -1.3); ctx.quadraticCurveTo(0, -1.55, 0.7, -1.3); ctx.moveTo(-1.2, 0.2); ctx.quadraticCurveTo(-1.28, 1.0, -1.25, 1.6); ctx.stroke(); ctx.globalAlpha = 1;
  } else if (style === "ponytail") {
    ctx.beginPath();
    ctx.moveTo(-1.06, -0.1);
    ctx.bezierCurveTo(-1.2, -1.0, -0.7, -1.5, 0.05, -1.5);
    ctx.bezierCurveTo(0.8, -1.5, 1.22, -1.0, 1.08, -0.1);
    ctx.bezierCurveTo(1.0, -0.5, 0.75, -0.95, 0.15, -0.95);
    ctx.bezierCurveTo(-0.5, -0.95, -0.95, -0.55, -1.06, -0.1);
    ctx.closePath();
    ctx.fill();
    ctx.fillStyle = shade(c, 0.5); ctx.globalAlpha = 0.5;
    ctx.fillRect(1.0, -0.62, 0.2, 0.12); ctx.globalAlpha = 1;
  } else if (style === "hijab") {
    // the scarf over the head: the face shows through an oval opening
    const s = spec.scarf;
    ctx.beginPath();
    ctx.moveTo(-1.25, 0.5);
    ctx.bezierCurveTo(-1.5, -1.3, -0.85, -1.75, 0, -1.75);
    ctx.bezierCurveTo(0.85, -1.75, 1.5, -1.3, 1.25, 0.5);
    ctx.bezierCurveTo(1.22, 1.15, 1.08, 1.5, 0.95, 1.62);
    ctx.quadraticCurveTo(0, 1.9, -0.95, 1.62);
    ctx.bezierCurveTo(-1.08, 1.5, -1.22, 1.15, -1.25, 0.5);
    ctx.closePath();
    ctx.moveTo(0, -0.98);
    ctx.bezierCurveTo(0.66, -0.98, 0.9, -0.55, 0.9, 0.1);
    ctx.bezierCurveTo(0.9, 0.8, 0.5, 1.22, 0, 1.24);
    ctx.bezierCurveTo(-0.5, 1.22, -0.9, 0.8, -0.9, 0.1);
    ctx.bezierCurveTo(-0.9, -0.55, -0.66, -0.98, 0, -0.98);
    ctx.closePath();
    const g = ctx.createLinearGradient(-1.4, -1.7, 1.4, 1.6);
    g.addColorStop(0, shade(s, 0.12)); g.addColorStop(1, shade(s, -0.12));
    ctx.fillStyle = g;
    ctx.fill("evenodd");
    ctx.strokeStyle = "rgba(255,255,255,0.14)"; ctx.lineWidth = 0.035;
    ctx.beginPath(); ctx.moveTo(-1.1, -0.4); ctx.quadraticCurveTo(-1.2, 0.5, -1.0, 1.2); ctx.moveTo(1.1, -0.45); ctx.quadraticCurveTo(1.18, 0.5, 1.0, 1.25); ctx.stroke();
  }
}

function drawEars(ctx, spec) {
  if (spec.hairStyle === "hijab") return;
  ctx.fillStyle = shade(spec.skin, -0.06);
  [-1, 1].forEach((side) => { ell(ctx, side * 1.0, 0.1, 0.13, 0.29); ctx.fill(); });
  ctx.fillStyle = shade(spec.skin, -0.2); ctx.globalAlpha = 0.5;
  [-1, 1].forEach((side) => { ell(ctx, side * 1.01, 0.12, 0.05, 0.15); ctx.fill(); });
  ctx.globalAlpha = 1;
}

function drawBeard(ctx, spec) {
  if (spec.beard) {
    ctx.save();
    ctx.beginPath();
    ctx.moveTo(-0.99, 0.0);
    ctx.bezierCurveTo(-1.02, 0.8, -0.55, 1.36, 0, 1.38);
    ctx.bezierCurveTo(0.55, 1.36, 1.02, 0.8, 0.99, 0.0);
    ctx.bezierCurveTo(0.9, 0.3, 0.78, 0.46, 0.55, 0.52);
    ctx.quadraticCurveTo(0.3, 0.5, 0.18, 0.56);
    ctx.quadraticCurveTo(0, 0.5, -0.18, 0.56);
    ctx.quadraticCurveTo(-0.3, 0.5, -0.55, 0.52);
    ctx.bezierCurveTo(-0.78, 0.46, -0.9, 0.3, -0.99, 0.0);
    ctx.closePath();
    if (spec.beard === "stubble") { ctx.fillStyle = shade(spec.hair, 0.0); ctx.globalAlpha = 0.2; } else ctx.fillStyle = spec.hair;
    ctx.fill();
    ctx.restore();
  }
  if (spec.mustache) {
    ctx.fillStyle = spec.hair;
    ctx.beginPath();
    ctx.moveTo(0, 0.5);
    ctx.bezierCurveTo(0.25, 0.42, 0.5, 0.48, 0.55, 0.6);
    ctx.bezierCurveTo(0.4, 0.58, 0.2, 0.6, 0, 0.56);
    ctx.bezierCurveTo(-0.2, 0.6, -0.4, 0.58, -0.55, 0.6);
    ctx.bezierCurveTo(-0.5, 0.48, -0.25, 0.42, 0, 0.5);
    ctx.closePath();
    ctx.fill();
  }
}

// mouth = { open, wide, round } (0..1). The mouth sits at y = 0.68.
function drawMouth(ctx, spec, mouth) {
  const lip = spec.lips || shade(spec.skin, -0.32);
  const my = 0.7;
  const opening = Math.max(0, mouth.open);
  const halfWidth = 0.3 + 0.12 * mouth.wide - 0.1 * mouth.round + 0.03 * opening;
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  if (opening < 0.05) {
    const smile = 0.035 + 0.04 * mouth.wide;
    ctx.strokeStyle = lip;
    ctx.lineWidth = spec.lips ? 0.085 : 0.06;
    ctx.beginPath();
    ctx.moveTo(-halfWidth, my - smile * 0.4);
    ctx.quadraticCurveTo(0, my + smile * 1.5, halfWidth, my - smile * 0.4);
    ctx.stroke();
    if (spec.lips) { ctx.strokeStyle = shade(lip, 0.28); ctx.globalAlpha = 0.5; ctx.lineWidth = 0.03; ctx.beginPath(); ctx.moveTo(-halfWidth * 0.45, my + 0.05); ctx.quadraticCurveTo(0, my + 0.1, halfWidth * 0.45, my + 0.05); ctx.stroke(); ctx.globalAlpha = 1; }
    return;
  }
  const h = 0.05 + opening * (0.36 + 0.12 * mouth.round);
  const topY = my - h * 0.3;
  const botY = my + h * 0.7;
  const shape = () => {
    ctx.beginPath();
    ctx.moveTo(-halfWidth, my);
    ctx.bezierCurveTo(-halfWidth * 0.55, topY - 0.045, halfWidth * 0.55, topY - 0.045, halfWidth, my);
    ctx.bezierCurveTo(halfWidth * (0.7 + 0.2 * mouth.round), botY + h * 0.35, -halfWidth * (0.7 + 0.2 * mouth.round), botY + h * 0.35, -halfWidth, my);
    ctx.closePath();
  };
  ctx.save();
  shape();
  ctx.fillStyle = "#3a0f18";
  ctx.fill();
  ctx.clip();
  // the upper teeth hang from the top lip; the lower ones show when the mouth is wide open; the tongue lies at the bottom
  const teeth = ctx.createLinearGradient(0, topY, 0, topY + 0.2);
  teeth.addColorStop(0, "#fbf9f4"); teeth.addColorStop(1, "#e4ded3");
  ctx.fillStyle = teeth;
  ctx.fillRect(-halfWidth * 0.86, topY - 0.02, halfWidth * 1.72, Math.min(0.17, 0.05 + h * 0.42));
  if (opening > 0.5) { ctx.fillStyle = "#efeae0"; ctx.fillRect(-halfWidth * 0.62, botY + h * 0.35 - 0.06, halfWidth * 1.24, 0.07); }
  ctx.fillStyle = "#c75468";
  ell(ctx, 0, botY + h * 0.18, halfWidth * 0.62, Math.max(0.03, h * 0.34));
  ctx.fill();
  ctx.fillStyle = "rgba(255,255,255,0.14)";
  ell(ctx, -halfWidth * 0.15, botY + h * 0.08, halfWidth * 0.3, Math.max(0.01, h * 0.1));
  ctx.fill();
  ctx.restore();
  ctx.strokeStyle = lip;
  ctx.lineWidth = spec.lips ? 0.07 : 0.05;
  shape();
  ctx.stroke();
  if (spec.lips) {                                           // the fuller upper lip
    ctx.fillStyle = lip; ctx.globalAlpha = 0.55;
    ctx.beginPath(); ctx.moveTo(-halfWidth, my); ctx.bezierCurveTo(-halfWidth * 0.55, topY - 0.045, halfWidth * 0.55, topY - 0.045, halfWidth, my);
    ctx.bezierCurveTo(halfWidth * 0.5, topY - 0.14, -halfWidth * 0.5, topY - 0.14, -halfWidth, my); ctx.fill(); ctx.globalAlpha = 1;
  }
}

function drawNose(ctx, spec) {
  ctx.strokeStyle = shade(spec.skin, -0.3);
  ctx.globalAlpha = 0.5;
  ctx.lineWidth = 0.045;
  ctx.lineCap = "round";
  ctx.beginPath(); ctx.moveTo(0.02, -0.02); ctx.quadraticCurveTo(0.13, 0.22, 0.1, 0.32); ctx.stroke();
  ctx.lineWidth = 0.05;
  ctx.beginPath(); ctx.moveTo(-0.12, 0.38); ctx.quadraticCurveTo(0, 0.46, 0.12, 0.38); ctx.stroke();
  ctx.globalAlpha = 1;
}

// blink: 0 open .. 1 shut. gaze: roughly -1..1.
function drawEyes(ctx, spec, blink, gazeX, gazeY) {
  [-1, 1].forEach((side) => {
    const x = side * 0.4;
    const y = -0.1;
    const openness = Math.max(0.04, 1 - blink);
    const rx = 0.2;
    const ry = 0.125 * openness;
    ctx.fillStyle = "#fbfbf9";
    ctx.beginPath();
    ctx.moveTo(x - rx, y + 0.01);
    ctx.quadraticCurveTo(x, y - ry * 2.25, x + rx, y + 0.01);
    ctx.quadraticCurveTo(x, y + ry * 1.8, x - rx, y + 0.01);
    ctx.closePath();
    ctx.fill();
    ctx.save();
    ctx.clip();
    const ix = x + gazeX * 0.035;
    const iy = y + gazeY * 0.025;
    ctx.fillStyle = spec.eye;
    ell(ctx, ix, iy, 0.092, 0.092); ctx.fill();
    ctx.fillStyle = "#0b0b0d";
    ell(ctx, ix, iy, 0.048, 0.048); ctx.fill();
    ctx.fillStyle = "rgba(255,255,255,0.9)";
    ell(ctx, ix - 0.03, iy - 0.035, 0.02, 0.02); ctx.fill();
    ctx.restore();
    // the upper lid and the lashes
    ctx.strokeStyle = shade(spec.hair === "#c3c7cc" ? "#555555" : spec.hair, -0.1);
    ctx.lineCap = "round";
    ctx.lineWidth = spec.lashes ? 0.055 : 0.04;
    ctx.beginPath();
    ctx.moveTo(x - rx, y + 0.01);
    ctx.quadraticCurveTo(x, y - ry * 2.25, x + rx, y + 0.01);
    ctx.stroke();
    if (spec.lashes) {
      ctx.lineWidth = 0.045;
      ctx.beginPath(); ctx.moveTo(x + side * rx, y + 0.01); ctx.lineTo(x + side * (rx + 0.075), y - 0.045); ctx.stroke();
    }
    ctx.strokeStyle = shade(spec.skin, -0.3); ctx.globalAlpha = 0.45; ctx.lineWidth = 0.03;
    ctx.beginPath(); ctx.moveTo(x - rx * 0.8, y - 0.17 * openness - 0.05); ctx.quadraticCurveTo(x, y - 0.24 * openness - 0.1, x + rx * 0.8, y - 0.17 * openness - 0.05); ctx.stroke(); ctx.globalAlpha = 1;
    if (blink > 0.8) {                                   // shut: the lid is a skin-coloured fold with a lash line
      ctx.fillStyle = shade(spec.skin, -0.02);
      ctx.beginPath(); ctx.ellipse(x, y - 0.01, rx * 1.04, 0.1, 0, 0, Math.PI * 2); ctx.fill();
      ctx.strokeStyle = shade(spec.hair === "#c3c7cc" ? "#555555" : spec.hair, -0.1); ctx.lineWidth = 0.05;
      ctx.beginPath(); ctx.moveTo(x - rx, y + 0.0); ctx.quadraticCurveTo(x, y + 0.07, x + rx, y + 0.0); ctx.stroke();
    }
  });
}

function drawBrows(ctx, spec, lift) {
  const color = spec.hairStyle === "hijab" ? "#2a2018" : spec.hair === "#c3c7cc" ? "#8c9096" : spec.hair;
  ctx.strokeStyle = color;
  ctx.lineCap = "round";
  ctx.lineWidth = 0.05 + 0.03 * (spec.brows || 1);
  [-1, 1].forEach((side) => {
    const x = side * 0.4;
    const y = -0.4 - lift * 0.07;
    ctx.beginPath();
    ctx.moveTo(x - side * 0.27, y + 0.06 - lift * 0.01);
    ctx.quadraticCurveTo(x - side * 0.02, y - 0.06 - lift * 0.03, x + side * 0.26, y + 0.04 + lift * 0.02);
    ctx.stroke();
  });
}

function drawGlasses(ctx, spec) {
  if (!spec.glasses) return;
  ctx.strokeStyle = spec.glasses === "round" ? "#9a6b3a" : "#2b2d33";
  ctx.lineWidth = 0.055;
  ctx.lineJoin = "round";
  [-1, 1].forEach((side) => {
    const x = side * 0.4;
    if (spec.glasses === "round") ell(ctx, x, -0.08, 0.3, 0.3);
    else { ctx.beginPath(); ctx.roundRect ? ctx.roundRect(x - 0.31, -0.3, 0.62, 0.44, 0.1) : ctx.rect(x - 0.31, -0.3, 0.62, 0.44); }
    ctx.fillStyle = "rgba(190,220,255,0.10)";
    ctx.fill();
    ctx.stroke();
    ctx.save();
    ctx.clip();
    ctx.fillStyle = "rgba(255,255,255,0.16)";
    ctx.beginPath(); ctx.moveTo(x - 0.28, -0.3); ctx.lineTo(x - 0.08, -0.3); ctx.lineTo(x - 0.28, -0.04); ctx.closePath(); ctx.fill();
    ctx.restore();
  });
  ctx.beginPath(); ctx.moveTo(-0.1, -0.1); ctx.quadraticCurveTo(0, -0.17, 0.1, -0.1); ctx.stroke();
  ctx.beginPath(); ctx.moveTo(-0.7, -0.12); ctx.lineTo(-1.0, -0.1); ctx.moveTo(0.7, -0.12); ctx.lineTo(1.0, -0.1); ctx.stroke();
}

// `layout`: { x, y, size } - the centre of the face in pixels and the size of one face unit in pixels.
// `state`:  { mouth: {open, wide, round}, blink, pose: headPose(...) }
export function drawPresenter(ctx, spec, state, layout) {
  const { mouth, blink, pose } = state;
  ctx.save();
  ctx.translate(layout.x, layout.y);
  ctx.scale(layout.size, layout.size);
  ctx.lineJoin = "round";
  ctx.lineCap = "round";

  ctx.save();
  ctx.translate(pose.dx * 2, pose.dy * 1.2);
  drawHairBack(ctx, spec);
  ctx.restore();

  ctx.save();
  ctx.translate(pose.dx * 1.2, pose.dy * 0.6);
  drawTorso(ctx, spec, pose.breathe);
  ctx.restore();

  ctx.save();
  ctx.translate(pose.dx * 2, pose.dy * 2);
  ctx.translate(0, 1.55);
  ctx.rotate(pose.rot);
  ctx.translate(0, -1.55);

  // the head's own shadow on the neck
  ctx.fillStyle = "rgba(0,0,0,0.2)";
  ell(ctx, 0, 1.3, 0.4, 0.17); ctx.fill();

  drawEars(ctx, spec);
  const skin = ctx.createRadialGradient(-0.35, -0.5, 0.1, 0, 0, 1.9);
  skin.addColorStop(0, shade(spec.skin, 0.1));
  skin.addColorStop(0.6, spec.skin);
  skin.addColorStop(1, shade(spec.skin, -0.14));
  headPath(ctx);
  ctx.fillStyle = skin;
  ctx.fill();
  // cheeks
  [-1, 1].forEach((side) => {
    const blush = ctx.createRadialGradient(side * 0.55, 0.4, 0, side * 0.55, 0.4, 0.34);
    blush.addColorStop(0, "rgba(220,90,90,0.2)"); blush.addColorStop(1, "rgba(220,90,90,0)");
    ctx.fillStyle = blush;
    ctx.fillRect(side * 0.55 - 0.4, 0.0, 0.8, 0.8);
  });
  drawBeard(ctx, spec);
  drawNose(ctx, spec);
  drawMouth(ctx, spec, mouth);
  drawEyes(ctx, spec, blink, pose.gazeX, pose.gazeY);
  drawBrows(ctx, spec, pose.brow);
  drawGlasses(ctx, spec);
  drawHairFront(ctx, spec);
  ctx.restore();
  ctx.restore();
}

// A small picture of a presenter for the picker (face, shoulders, a gentle smile).
export function drawPresenterThumb(canvas, spec) {
  const ctx = canvas.getContext("2d");
  const { width: w, height: h } = canvas;
  const g = ctx.createLinearGradient(0, 0, w, h);
  g.addColorStop(0, "#16324f"); g.addColorStop(1, "#0a1a2e");
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, w, h);
  drawPresenter(ctx, spec, { mouth: { open: 0, wide: 0.5, round: 0 }, blink: 0, pose: { dx: 0, dy: 0, rot: 0, breathe: 1, brow: 0, gazeX: 0, gazeY: 0 } }, { x: w / 2, y: h * 0.4, size: w * 0.2 });
}
